import { appState } from "./state";
import { notifyStateChange } from "./stateBus";
import { apiFetch } from "./api";
import { requestLock } from "./lockBus";
import type { CreditsLedgerEntry, DailyRewardResult } from "./types";

// Framework-agnostic credits data layer, same shape as giftsApi.ts/albumsApi.ts
// - appState.credits/creditsStreak are a read cache for display only. The
// server is the sole source of truth for the actual balance (server.js's
// CreditsDB); nothing here ever computes or guesses a new balance itself.

export async function loadCredits(): Promise<void> {
  try {
    const res = await apiFetch("/api/credits");
    if (res.status === 401) {
      requestLock("unauthorized");
      return;
    }
    if (!res.ok) return;
    const data = await res.json();
    appState.credits = typeof data.balance === "number" ? data.balance : 0;
    if (data.streak) appState.creditsStreak = data.streak;
    notifyStateChange();
  } catch {
    // Best effort - the next resync (onSocketOpen, credits_updated push)
    // retries.
  }
}

export async function loadGiftCatalog(): Promise<void> {
  try {
    const res = await apiFetch("/api/gifts/catalog");
    if (res.status === 401) {
      requestLock("unauthorized");
      return;
    }
    if (!res.ok) return;
    const data = await res.json();
    appState.giftCatalog = Array.isArray(data.catalog) ? data.catalog : [];
    notifyStateChange();
  } catch {
    // Best effort.
  }
}

export async function loadCreditsLedger(): Promise<CreditsLedgerEntry[]> {
  try {
    const res = await apiFetch("/api/credits/ledger");
    if (res.status === 401) {
      requestLock("unauthorized");
      return [];
    }
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.ledger) ? data.ledger : [];
  } catch {
    return [];
  }
}

// Pushed via the credits_updated WS event (chatSocket.ts) - lets a second
// open tab/device of the SAME user pick up a balance change made elsewhere
// (e.g. a virtual gift sent from another tab) without a manual refresh.
export function applyCreditsUpdated(balance: number): void {
  appState.credits = balance;
  notifyStateChange();
}

// Surfaced by both POST /api/unlock and GET /api/me (see server.js's
// comments on why both hook points exist) - AuthGate.tsx reads this off
// whichever response actually ran during boot and shows the reward toast.
// Also applies the resulting balance immediately, matching PART 30's "no
// optimistic invention" rule - this IS the authoritative server value, not
// a guess.
export function applyDailyReward(reward: DailyRewardResult | null): void {
  if (!reward) return;
  appState.credits = reward.balance;
  appState.creditsStreak = {
    lastRewardDate: null, // not returned by this shape - loadCredits() will fill it in properly on next fetch
    currentStreak: reward.streak,
    longestStreak: reward.longestStreak,
  };
  notifyStateChange();
}
